import React, { useRef, useState } from 'react';
import { Alert, List, ListItem, ListItemText, Typography, Paper } from '@mui/material';

interface Props {
  onImport: (file: File) => Promise<void>;
}

interface ImportError {
  row: number;
  message: string;
  data?: Record<string, unknown>;
}

const FileImport: React.FC<Props> = ({ onImport }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processedCount, setProcessedCount] = useState<{ total: number; success: number; failed: number }>({
    total: 0,
    success: 0,
    failed: 0
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setError(null);
      setImportErrors([]);
      setSuccess(false);
      setLoading(true);
      setProcessedCount({ total: 0, success: 0, failed: 0 });
      
      try {
        await onImport(file);
        setSuccess(true);
        // Input'u temizle
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (err: any) {
        const errorData = err.response?.data;
        
        if (errorData?.details?.errors) {
          setImportErrors(errorData.details.errors);
        }
        
        setError(errorData?.message || 'Dosya yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="file-import">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
      />
      <div className="import-buttons">
        <button 
          className="btn btn-primary me-2"
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
        >
          {loading ? 'Yükleniyor...' : 'Excel/CSV Dosyası Seç'}
        </button>
      </div>
      <small className="text-muted">
        Desteklenen formatlar: .xlsx, .xls, .csv
      </small>

      {error && (
        <Alert severity="error" style={{ marginTop: '10px' }}>
          {error}
        </Alert>
      )}
      
      {importErrors.length > 0 && (
        <Paper elevation={2} style={{ marginTop: '10px', padding: '10px' }}>
          <Typography variant="subtitle1" color="error" gutterBottom>
            Hatalı Kayıtlar ({importErrors.length}):
          </Typography>
          <List dense>
            {importErrors.map((err, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={`Satır ${err.row}: ${err.message}`}
                  secondary={err.data ? `Veri: ${JSON.stringify(err.data)}` : undefined}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      
      {success && (
        <Alert 
          severity="success" 
          style={{ 
            marginTop: '10px',
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            border: '1px solid #a5d6a7'
          }}
        >
          <strong>Başarılı!</strong> Dosya başarıyla yüklendi
          {processedCount.total > 0 && (
            <div style={{ marginTop: '5px' }}>
              <small>
                Toplam: {processedCount.total} kayıt<br />
                Başarılı: {processedCount.success} kayıt<br />
                Başarısız: {processedCount.failed} kayıt
              </small>
            </div>
          )}
        </Alert>
      )}
    </div>
  );
};

export default FileImport; 